import React, { useEffect, useRef, useState } from 'react'
import { AudioOutlined } from '@ant-design/icons'
import {
	AudioTrackPublication,
	LocalAudioTrack,
	LocalAudioTrackPublication,
	LocalVideoTrack,
	LocalVideoTrackPublication,
	Participant as IParticipant,
	RemoteAudioTrack,
	RemoteAudioTrackPublication,
	RemoteVideoTrack,
	RemoteVideoTrackPublication,
	VideoTrackPublication as IVideoTrackPublication,
} from 'twilio-video'
import { message } from 'antd'

type TVideoTrack = LocalVideoTrack | RemoteVideoTrack
type TAudioTrack = LocalAudioTrack | RemoteAudioTrack
type TTrack = TVideoTrack | TAudioTrack
type TVideoTrackPublication = LocalVideoTrackPublication | RemoteVideoTrackPublication
type TAudioTrackPublication = LocalAudioTrackPublication | RemoteAudioTrackPublication
type TTrackPublication = TVideoTrackPublication | TAudioTrackPublication

function ParticipantItem({
	participant,
	mainParticipant,
	localParticipant,
	dominantSpeaker,
	style,
}: {
	participant: IParticipant
	handleSetMain?: (p: IParticipant) => void
	mainParticipant: boolean
	localParticipant: boolean
	dominantSpeaker: boolean
	style: any
}) {
	const [videoTracks, setVideoTracks] = useState<TVideoTrack[]>([])
	const [videoTrackPublications, setVideoTrackPublications] = useState<TVideoTrackPublication[]>([])
	const [audioTracks, setAudioTracks] = useState<TAudioTrack[]>([])

	const videoRef = useRef<HTMLVideoElement>()
	const audioRef = useRef()
	const trackpubsToTracks = (trackMap: Map<string, IVideoTrackPublication> | Map<string, AudioTrackPublication>) => {
		const tracks: TTrack[] = []
		trackMap.forEach((publication: IVideoTrackPublication | AudioTrackPublication) => {
			if (!!publication.track) {
				tracks.push(publication.track)
			}
		})
		return tracks
	}

	useEffect(() => {
		if (participant) {
			setVideoTracks(trackpubsToTracks(participant.videoTracks) as TVideoTrack[])
			setAudioTracks(trackpubsToTracks(participant.audioTracks) as TAudioTrack[])

			const trackSubscribed = (track: TTrack) => {
				if (track.kind === 'video') {
					setVideoTracks((prebVideoTracks: TVideoTrack[]) => [...prebVideoTracks, track])
				} else if (track.kind === 'audio') {
					setAudioTracks((prevAudioTracks: TAudioTrack[]) => [...prevAudioTracks, track])
				}
			}

			const trackUnsubscribed = (track: TTrack) => {
				if (track.kind === 'video') {
					setVideoTrackPublications((prevVideoTrackPublications: TVideoTrackPublication[]) =>
						prevVideoTrackPublications.filter((v) => v.track !== track)
					)
					setVideoTracks((prevVideoTracks: TVideoTrack[]) => prevVideoTracks.filter((v) => v !== track))
				} else if (track.kind === 'audio') {
					setAudioTracks((prevAudioTracks: TAudioTrack[]) => prevAudioTracks.filter((a) => a !== track))
				}
			}

			const trackPublished = (track: TTrackPublication) => {
				if (track.kind === 'video') {
					setVideoTrackPublications((prevVideoTrackPublications: TVideoTrackPublication[]) => [
						...prevVideoTrackPublications,
						track,
					])
				}
			}

			const trackUnpublished = (track: TTrackPublication) => {
				if (track.kind === 'video') {
					setVideoTrackPublications((prevVideoTrackPublications: TVideoTrackPublication[]) =>
						prevVideoTrackPublications.filter((v) => v !== track)
					)
				}
			}

			const trackDisabledSubscribed = (track: TTrack) => {
				if (track.kind === 'video') {
					setVideoTracks((prevVideoTracks: TVideoTrack[]) => [...prevVideoTracks.filter((v) => v !== track), track])
				}
			}

			const reconnecting = () => {
				if (!localParticipant) {
					message.loading(`${participant.identity} Reconnecting...`, 2)
				}
			}

			const reconnected = () => {
				if (!localParticipant) {
					message.success(`${participant.identity} Connected...`, 2)
				}
			}

			participant.on('trackSubscribed', trackSubscribed)
			participant.on('trackUnsubscribed', trackUnsubscribed)
			participant.on('trackPublished', trackPublished)
			participant.on('trackUnpublished', trackUnpublished)
			participant.on('trackDisabled', trackDisabledSubscribed)
			participant.on('trackEnabled', trackDisabledSubscribed)
			participant.on('reconnecting', reconnecting)
			participant.on('reconnected', reconnected)

			return () => {
				setVideoTracks([])
				setAudioTracks([])
				setVideoTrackPublications([])
				participant.removeAllListeners()
			}
		}
	}, [participant])

	useEffect(() => {
		const videoTrack = videoTrackPublications[0]?.track || videoTracks.find((v) => v.isEnabled)
		if (videoTrack) {
			videoTrack.attach(videoRef.current)
			return () => {
				videoTrack.detach()
			}
		}
	}, [videoTracks, videoTrackPublications])

	useEffect(() => {
		const audioTrack = audioTracks[0]
		if (audioTrack) {
			audioTrack.attach(audioRef.current)
			return () => {
				audioTrack.detach()
			}
		}
	}, [audioTracks])

	return (
		<>
			<video ref={videoRef} style={style} autoPlay={true} />
			<audio ref={audioRef} autoPlay={true} />
			<div className="room-participant-name">
				{!mainParticipant && <AudioOutlined style={{ color: dominantSpeaker ? 'green' : 'grey' }} />}{' '}
				{participant?.identity}
			</div>
		</>
	)
}

export default ParticipantItem
